import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  Users,
  UserPlus,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Mail,
  Phone,
  Shield,
  Building2,
  Copy,
  CheckCircle,
  XCircle,
  Clock,
  Crown,
  UserCog,
  Briefcase,
  Tractor,
  Leaf,
  Eye,
  Camera,
  Upload,
  FileText,
  Euro,
  ThumbsUp,
  ThumbsDown,
  MessageSquare,
  Star,
  Calendar,
  Paperclip,
  Download,
  Plus,
  X,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspace } from '@/contexts/WorkspaceContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Mapping des icônes par rôle
const ROLE_ICONS = {
  admin: Crown,
  responsable: UserCog,
  technicien: Briefcase,
  operateur: Tractor,
  saisonnier: Leaf,
  consultant: Eye,
};

const ROLE_COLORS = {
  admin: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
  responsable: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
  technicien: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  operateur: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
  saisonnier: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
  consultant: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400',
};

// Dialog pour ajouter/modifier un collaborateur
const CollaborateurDialog = ({ 
  open, 
  onOpenChange, 
  collaborateur, 
  roles, 
  permissions,
  societes,
  onSave 
}) => {
  const [formData, setFormData] = useState({
    email: '',
    nom: '',
    prenom: '',
    role: 'operateur',
    telephone: '',
    societes_access: [],
    permissions: [],
    // Certiphyto
    certiphyto_numero: '',
    certiphyto_date_obtention: '',
    certiphyto_date_expiration: '',
    certiphyto_type: '',
    // Permis de conduire
    permis_categories: [],
    permis_date_obtention: '',
    permis_date_expiration: '',
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (collaborateur) {
      setFormData({
        email: collaborateur.email || '',
        nom: collaborateur.nom || '',
        prenom: collaborateur.prenom || '',
        role: collaborateur.role || 'operateur',
        telephone: collaborateur.telephone || '',
        societes_access: collaborateur.societes_access || [],
        permissions: collaborateur.permissions || [],
        // Certiphyto
        certiphyto_numero: collaborateur.certiphyto_numero || '',
        certiphyto_date_obtention: collaborateur.certiphyto_date_obtention || '',
        certiphyto_date_expiration: collaborateur.certiphyto_date_expiration || '',
        certiphyto_type: collaborateur.certiphyto_type || '',
        // Permis de conduire
        permis_categories: collaborateur.permis_categories || [],
        permis_date_obtention: collaborateur.permis_date_obtention || '',
        permis_date_expiration: collaborateur.permis_date_expiration || '',
      });
    } else {
      setFormData({
        email: '',
        nom: '',
        prenom: '',
        role: 'operateur',
        telephone: '',
        societes_access: [],
        permissions: [],
        certiphyto_numero: '',
        certiphyto_date_obtention: '',
        certiphyto_date_expiration: '',
        certiphyto_type: '',
        permis_categories: [],
        permis_date_obtention: '',
        permis_date_expiration: '',
      });
    }
  }, [collaborateur, open]);

  // Mettre à jour les permissions par défaut quand le rôle change
  useEffect(() => {
    if (roles[formData.role]) {
      setFormData(prev => ({
        ...prev,
        permissions: roles[formData.role].permissions || []
      }));
    }
  }, [formData.role, roles]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    await onSave(formData, collaborateur?.id);
    setSaving(false);
    onOpenChange(false);
  };

  const toggleSociete = (societeId) => {
    setFormData(prev => ({
      ...prev,
      societes_access: prev.societes_access.includes(societeId)
        ? prev.societes_access.filter(id => id !== societeId)
        : [...prev.societes_access, societeId]
    }));
  };

  const togglePermission = (perm) => {
    setFormData(prev => ({
      ...prev,
      permissions: prev.permissions.includes(perm)
        ? prev.permissions.filter(p => p !== perm)
        : [...prev.permissions, perm]
    }));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {collaborateur ? 'Modifier le collaborateur' : 'Ajouter un collaborateur'}
          </DialogTitle>
          <DialogDescription>
            {collaborateur 
              ? 'Modifiez les informations et les accès du collaborateur.'
              : 'Invitez un nouveau membre à rejoindre votre équipe.'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="prenom">Prénom *</Label>
              <Input
                id="prenom"
                value={formData.prenom}
                onChange={(e) => setFormData(prev => ({ ...prev, prenom: e.target.value }))}
                placeholder="Jean"
                required
                data-testid="input-prenom"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="nom">Nom *</Label>
              <Input
                id="nom"
                value={formData.nom}
                onChange={(e) => setFormData(prev => ({ ...prev, nom: e.target.value }))}
                placeholder="Dupont"
                required
                data-testid="input-nom"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="email">Email *</Label>
            <Input
              id="email"
              type="email"
              value={formData.email}
              onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
              placeholder="jean.dupont@example.com"
              required
              disabled={!!collaborateur}
              data-testid="input-email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="telephone">Téléphone</Label>
            <Input
              id="telephone"
              value={formData.telephone}
              onChange={(e) => setFormData(prev => ({ ...prev, telephone: e.target.value }))}
              placeholder="06 12 34 56 78"
              data-testid="input-telephone"
            />
          </div>

          {/* Section Certiphyto */}
          <div className="border rounded-lg p-4 space-y-4 bg-green-50/50 dark:bg-green-900/10">
            <h4 className="font-medium text-green-800 dark:text-green-400 flex items-center gap-2">
              <Leaf className="w-4 h-4" />
              Certiphyto
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="certiphyto_numero">N° Certiphyto</Label>
                <Input
                  id="certiphyto_numero"
                  value={formData.certiphyto_numero}
                  onChange={(e) => setFormData(prev => ({ ...prev, certiphyto_numero: e.target.value }))}
                  placeholder="Ex: CP-2024-XXXXX"
                  data-testid="input-certiphyto-numero"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="certiphyto_type">Type</Label>
                <Select 
                  value={formData.certiphyto_type} 
                  onValueChange={(value) => setFormData(prev => ({ ...prev, certiphyto_type: value }))}
                >
                  <SelectTrigger data-testid="select-certiphyto-type">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="decideur">Décideur en exploitation</SelectItem>
                    <SelectItem value="operateur">Opérateur</SelectItem>
                    <SelectItem value="conseil">Conseil</SelectItem>
                    <SelectItem value="vente">Mise en vente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="certiphyto_date_obtention">Date d'obtention</Label>
                <Input
                  id="certiphyto_date_obtention"
                  type="date"
                  value={formData.certiphyto_date_obtention}
                  onChange={(e) => setFormData(prev => ({ ...prev, certiphyto_date_obtention: e.target.value }))}
                  data-testid="input-certiphyto-obtention"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="certiphyto_date_expiration">Date d'expiration</Label>
                <Input
                  id="certiphyto_date_expiration"
                  type="date"
                  value={formData.certiphyto_date_expiration}
                  onChange={(e) => setFormData(prev => ({ ...prev, certiphyto_date_expiration: e.target.value }))}
                  data-testid="input-certiphyto-expiration"
                />
              </div>
            </div>
          </div>

          {/* Section Permis de conduire */}
          <div className="border rounded-lg p-4 space-y-4 bg-blue-50/50 dark:bg-blue-900/10">
            <h4 className="font-medium text-blue-800 dark:text-blue-400 flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              Permis de conduire
            </h4>
            <div className="space-y-2">
              <Label>Catégories</Label>
              <div className="flex flex-wrap gap-2">
                {['AM', 'A1', 'A2', 'A', 'B', 'BE', 'C', 'CE', 'C1', 'C1E', 'D', 'DE'].map((cat) => (
                  <div key={cat} className="flex items-center gap-1">
                    <Checkbox
                      id={`permis-${cat}`}
                      checked={formData.permis_categories.includes(cat)}
                      onCheckedChange={(checked) => {
                        setFormData(prev => ({
                          ...prev,
                          permis_categories: checked
                            ? [...prev.permis_categories, cat]
                            : prev.permis_categories.filter(c => c !== cat)
                        }));
                      }}
                    />
                    <Label htmlFor={`permis-${cat}`} className="text-sm cursor-pointer">{cat}</Label>
                  </div>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="permis_date_obtention">Date d'obtention</Label>
                <Input
                  id="permis_date_obtention"
                  type="date"
                  value={formData.permis_date_obtention}
                  onChange={(e) => setFormData(prev => ({ ...prev, permis_date_obtention: e.target.value }))}
                  data-testid="input-permis-obtention"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="permis_date_expiration">Date d'expiration</Label>
                <Input
                  id="permis_date_expiration"
                  type="date"
                  value={formData.permis_date_expiration}
                  onChange={(e) => setFormData(prev => ({ ...prev, permis_date_expiration: e.target.value }))}
                  data-testid="input-permis-expiration"
                />
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="role">Rôle *</Label>
            <Select 
              value={formData.role} 
              onValueChange={(value) => setFormData(prev => ({ ...prev, role: value }))}
            >
              <SelectTrigger data-testid="select-role">
                <SelectValue placeholder="Sélectionner un rôle" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(roles).map(([key, role]) => {
                  const RoleIcon = ROLE_ICONS[key];
                  return (
                    <SelectItem key={key} value={key}>
                      <div className="flex items-center gap-2">
                        {RoleIcon && <RoleIcon className="w-4 h-4" />}
                        <span>{role.nom}</span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            {roles[formData.role] && (
              <p className="text-xs text-muted-foreground">
                {roles[formData.role].description}
              </p>
            )}
          </div>

          {/* Sociétés accessibles */}
          {societes && societes.length > 1 && (
            <div className="space-y-2">
              <Label>Sociétés accessibles</Label>
              <div className="border rounded-lg p-3 space-y-2 max-h-32 overflow-y-auto">
                {societes.map((soc) => (
                  <div key={soc.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`soc-${soc.id}`}
                      checked={formData.societes_access.includes(soc.id)}
                      onCheckedChange={() => toggleSociete(soc.id)}
                    />
                    <Label htmlFor={`soc-${soc.id}`} className="text-sm cursor-pointer">
                      {soc.nom}
                    </Label>
                  </div>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                Si vide, accès à toutes les sociétés
              </p>
            </div>
          )}

          {/* Permissions personnalisées */}
          <div className="space-y-2">
            <Label>Permissions</Label>
            <div className="border rounded-lg p-3 grid grid-cols-2 gap-2 max-h-40 overflow-y-auto">
              {Object.entries(permissions).map(([key, desc]) => (
                <div key={key} className="flex items-center gap-2">
                  <Checkbox
                    id={`perm-${key}`}
                    checked={formData.permissions.includes(key) || formData.permissions.includes('all')}
                    onCheckedChange={() => togglePermission(key)}
                    disabled={formData.permissions.includes('all') && key !== 'all'}
                  />
                  <Label htmlFor={`perm-${key}`} className="text-sm cursor-pointer">
                    {key === 'all' ? 'Accès complet' : desc}
                  </Label>
                </div>
              ))}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" disabled={saving} data-testid="btn-save-collaborateur">
              {saving ? 'Enregistrement...' : (collaborateur ? 'Modifier' : 'Inviter')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Fiche complète du salarié (CRM RH)
const FicheSalarieDialog = ({ open, onOpenChange, collaborateur, onUpdate }) => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('profil');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const contractInputRef = useRef(null);
  
  const [ficheData, setFicheData] = useState({
    photo_url: '',
    salaire_brut: '',
    salaire_net: '',
    type_contrat: '',
    date_debut_contrat: '',
    date_fin_contrat: '',
    contrat_fichier: null,
    contrat_nom: '',
    points_forts: [],
    points_faibles: [],
    demandes: [],
    notes_rh: '',
    objectifs: '',
    evaluations: [],
  });
  
  const [newPointFort, setNewPointFort] = useState('');
  const [newPointFaible, setNewPointFaible] = useState('');
  const [newDemande, setNewDemande] = useState('');

  useEffect(() => {
    if (collaborateur) {
      setFicheData({
        photo_url: collaborateur.photo_url || '',
        salaire_brut: collaborateur.salaire_brut || '',
        salaire_net: collaborateur.salaire_net || '',
        type_contrat: collaborateur.type_contrat || '',
        date_debut_contrat: collaborateur.date_debut_contrat || '',
        date_fin_contrat: collaborateur.date_fin_contrat || '',
        contrat_fichier: collaborateur.contrat_fichier || null,
        contrat_nom: collaborateur.contrat_nom || '',
        points_forts: collaborateur.points_forts || [],
        points_faibles: collaborateur.points_faibles || [],
        demandes: collaborateur.demandes || [],
        notes_rh: collaborateur.notes_rh || '',
        objectifs: collaborateur.objectifs || '',
        evaluations: collaborateur.evaluations || [],
      });
    }
  }, [collaborateur, open]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/upload/image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setFicheData(prev => ({ ...prev, photo_url: data.url }));
        toast.success('Photo uploadée');
      } else {
        toast.error('Erreur lors de l\'upload');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const handleContractUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await fetch(`${API_URL}/api/upload/document`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setFicheData(prev => ({ 
          ...prev, 
          contrat_fichier: data.url,
          contrat_nom: file.name 
        }));
        toast.success('Contrat uploadé');
      } else {
        toast.error('Erreur lors de l\'upload');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
    }
  };

  const addPointFort = () => {
    if (newPointFort.trim()) {
      setFicheData(prev => ({
        ...prev,
        points_forts: [...prev.points_forts, newPointFort.trim()]
      }));
      setNewPointFort('');
    }
  };

  const addPointFaible = () => {
    if (newPointFaible.trim()) {
      setFicheData(prev => ({
        ...prev,
        points_faibles: [...prev.points_faibles, newPointFaible.trim()]
      }));
      setNewPointFaible('');
    }
  };

  const addDemande = () => {
    if (newDemande.trim()) {
      setFicheData(prev => ({
        ...prev,
        demandes: [...prev.demandes, { 
          texte: newDemande.trim(), 
          date: new Date().toISOString().split('T')[0],
          statut: 'en_attente'
        }]
      }));
      setNewDemande('');
    }
  };

  const removeItem = (field, index) => {
    setFicheData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const updateDemandeStatut = (index, statut) => {
    setFicheData(prev => ({
      ...prev,
      demandes: prev.demandes.map((d, i) => i === index ? { ...d, statut } : d)
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/team/collaborateurs/${collaborateur.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ficheData)
      });
      
      if (response.ok) {
        toast.success('Fiche salarié mise à jour');
        onUpdate && onUpdate();
        onOpenChange(false);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (!collaborateur) return null;

  const initials = `${collaborateur.prenom?.[0] || ''}${collaborateur.nom?.[0] || ''}`.toUpperCase();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="w-12 h-12">
              <AvatarImage src={ficheData.photo_url} />
              <AvatarFallback className="bg-primary/10 text-primary text-lg">{initials}</AvatarFallback>
            </Avatar>
            <div>
              <span>Fiche de {collaborateur.prenom} {collaborateur.nom}</span>
              <Badge className="ml-2" variant="outline">{collaborateur.role}</Badge>
            </div>
          </DialogTitle>
          <DialogDescription>
            Gestion complète du salarié - Informations confidentielles RH
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="mt-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="profil" className="flex items-center gap-2">
              <Camera className="w-4 h-4" />
              Profil
            </TabsTrigger>
            <TabsTrigger value="contrat" className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              Contrat
            </TabsTrigger>
            <TabsTrigger value="evaluation" className="flex items-center gap-2">
              <Star className="w-4 h-4" />
              Évaluation
            </TabsTrigger>
            <TabsTrigger value="demandes" className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              Demandes
            </TabsTrigger>
          </TabsList>

          {/* Onglet Profil */}
          <TabsContent value="profil" className="space-y-6 mt-4">
            <div className="flex items-start gap-6">
              {/* Photo de profil */}
              <div className="flex flex-col items-center gap-2">
                <Avatar className="w-32 h-32 border-4 border-primary/20">
                  <AvatarImage src={ficheData.photo_url} />
                  <AvatarFallback className="bg-primary/10 text-primary text-3xl">{initials}</AvatarFallback>
                </Avatar>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                >
                  <Camera className="w-4 h-4 mr-2" />
                  {uploading ? 'Upload...' : 'Changer la photo'}
                </Button>
              </div>

              {/* Infos de base */}
              <div className="flex-1 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-muted-foreground">Nom complet</Label>
                    <p className="font-medium">{collaborateur.prenom} {collaborateur.nom}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Email</Label>
                    <p className="font-medium">{collaborateur.email}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Téléphone</Label>
                    <p className="font-medium">{collaborateur.telephone || 'Non renseigné'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Rôle</Label>
                    <Badge className={ROLE_COLORS[collaborateur.role]}>{collaborateur.role}</Badge>
                  </div>
                </div>
                
                {/* Certiphyto & Permis résumé */}
                {collaborateur.certiphyto_numero && (
                  <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-lg">
                    <Label className="text-green-700 dark:text-green-400 flex items-center gap-2">
                      <Leaf className="w-4 h-4" />
                      Certiphyto: {collaborateur.certiphyto_numero}
                    </Label>
                    <p className="text-sm text-muted-foreground">
                      Expire le: {collaborateur.certiphyto_date_expiration || 'Non renseigné'}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Onglet Contrat */}
          <TabsContent value="contrat" className="space-y-6 mt-4">
            <div className="grid grid-cols-2 gap-6">
              {/* Informations contrat */}
              <div className="space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Informations du contrat
                </h4>
                
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Type de contrat</Label>
                    <Select 
                      value={ficheData.type_contrat} 
                      onValueChange={(v) => setFicheData(prev => ({ ...prev, type_contrat: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionner" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cdi">CDI</SelectItem>
                        <SelectItem value="cdd">CDD</SelectItem>
                        <SelectItem value="saisonnier">Saisonnier</SelectItem>
                        <SelectItem value="apprenti">Apprentissage</SelectItem>
                        <SelectItem value="stage">Stage</SelectItem>
                        <SelectItem value="interim">Intérim</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-2">
                      <Label>Date de début</Label>
                      <Input
                        type="date"
                        value={ficheData.date_debut_contrat}
                        onChange={(e) => setFicheData(prev => ({ ...prev, date_debut_contrat: e.target.value }))}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Date de fin</Label>
                      <Input
                        type="date"
                        value={ficheData.date_fin_contrat}
                        onChange={(e) => setFicheData(prev => ({ ...prev, date_fin_contrat: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Salaire */}
              <div className="space-y-4">
                <h4 className="font-medium flex items-center gap-2">
                  <Euro className="w-4 h-4" />
                  Rémunération
                </h4>
                
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label>Salaire brut mensuel (€)</Label>
                    <Input
                      type="number"
                      placeholder="Ex: 2500"
                      value={ficheData.salaire_brut}
                      onChange={(e) => setFicheData(prev => ({ ...prev, salaire_brut: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Salaire net mensuel (€)</Label>
                    <Input
                      type="number"
                      placeholder="Ex: 1950"
                      value={ficheData.salaire_net}
                      onChange={(e) => setFicheData(prev => ({ ...prev, salaire_net: e.target.value }))}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Contrat en pièce jointe */}
            <div className="border rounded-lg p-4 space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <Paperclip className="w-4 h-4" />
                Contrat de travail (pièce jointe)
              </h4>
              
              <input
                ref={contractInputRef}
                type="file"
                accept=".pdf,.doc,.docx"
                onChange={handleContractUpload}
                className="hidden"
              />
              
              {ficheData.contrat_fichier ? (
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-primary" />
                    <span>{ficheData.contrat_nom || 'Contrat.pdf'}</span>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" asChild>
                      <a href={ficheData.contrat_fichier} target="_blank" rel="noopener noreferrer">
                        <Download className="w-4 h-4 mr-1" />
                        Télécharger
                      </a>
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => contractInputRef.current?.click()}
                    >
                      <Upload className="w-4 h-4 mr-1" />
                      Remplacer
                    </Button>
                  </div>
                </div>
              ) : (
                <Button 
                  variant="outline" 
                  onClick={() => contractInputRef.current?.click()}
                  disabled={uploading}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  {uploading ? 'Upload...' : 'Joindre le contrat'}
                </Button>
              )}
            </div>
          </TabsContent>

          {/* Onglet Évaluation */}
          <TabsContent value="evaluation" className="space-y-6 mt-4">
            <div className="grid grid-cols-2 gap-6">
              {/* Points forts */}
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2 text-green-600">
                  <ThumbsUp className="w-4 h-4" />
                  Points forts
                </h4>
                <div className="flex gap-2">
                  <Input
                    placeholder="Ajouter un point fort..."
                    value={newPointFort}
                    onChange={(e) => setNewPointFort(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addPointFort()}
                  />
                  <Button size="icon" onClick={addPointFort}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {ficheData.points_forts.map((point, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-green-50 dark:bg-green-900/20 rounded-lg">
                      <span className="text-sm">{point}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem('points_forts', idx)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  {ficheData.points_forts.length === 0 && (
                    <p className="text-sm text-muted-foreground italic">Aucun point fort renseigné</p>
                  )}
                </div>
              </div>

              {/* Points faibles */}
              <div className="space-y-3">
                <h4 className="font-medium flex items-center gap-2 text-orange-600">
                  <ThumbsDown className="w-4 h-4" />
                  Points à améliorer
                </h4>
                <div className="flex gap-2">
                  <Input
                    placeholder="Ajouter un point à améliorer..."
                    value={newPointFaible}
                    onChange={(e) => setNewPointFaible(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addPointFaible()}
                  />
                  <Button size="icon" onClick={addPointFaible}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {ficheData.points_faibles.map((point, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-orange-50 dark:bg-orange-900/20 rounded-lg">
                      <span className="text-sm">{point}</span>
                      <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeItem('points_faibles', idx)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ))}
                  {ficheData.points_faibles.length === 0 && (
                    <p className="text-sm text-muted-foreground italic">Aucun point à améliorer renseigné</p>
                  )}
                </div>
              </div>
            </div>

            {/* Notes RH et Objectifs */}
            <div className="grid grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>Notes RH (confidentielles)</Label>
                <Textarea
                  placeholder="Notes privées sur le salarié..."
                  value={ficheData.notes_rh}
                  onChange={(e) => setFicheData(prev => ({ ...prev, notes_rh: e.target.value }))}
                  rows={4}
                />
              </div>
              <div className="space-y-2">
                <Label>Objectifs</Label>
                <Textarea
                  placeholder="Objectifs pour ce salarié..."
                  value={ficheData.objectifs}
                  onChange={(e) => setFicheData(prev => ({ ...prev, objectifs: e.target.value }))}
                  rows={4}
                />
              </div>
            </div>
          </TabsContent>

          {/* Onglet Demandes */}
          <TabsContent value="demandes" className="space-y-6 mt-4">
            <div className="space-y-3">
              <h4 className="font-medium flex items-center gap-2">
                <MessageSquare className="w-4 h-4" />
                Demandes du salarié
              </h4>
              
              <div className="flex gap-2">
                <Input
                  placeholder="Nouvelle demande du salarié..."
                  value={newDemande}
                  onChange={(e) => setNewDemande(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addDemande()}
                />
                <Button onClick={addDemande}>
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter
                </Button>
              </div>

              <div className="space-y-3 max-h-60 overflow-y-auto">
                {ficheData.demandes.map((demande, idx) => (
                  <div key={idx} className="flex items-start justify-between p-3 border rounded-lg">
                    <div className="flex-1">
                      <p className="text-sm">{demande.texte}</p>
                      <p className="text-xs text-muted-foreground mt-1">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {demande.date}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Select 
                        value={demande.statut} 
                        onValueChange={(v) => updateDemandeStatut(idx, v)}
                      >
                        <SelectTrigger className="w-32 h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="en_attente">
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3 text-amber-500" />
                              En attente
                            </span>
                          </SelectItem>
                          <SelectItem value="acceptee">
                            <span className="flex items-center gap-1">
                              <CheckCircle className="w-3 h-3 text-green-500" />
                              Acceptée
                            </span>
                          </SelectItem>
                          <SelectItem value="refusee">
                            <span className="flex items-center gap-1">
                              <XCircle className="w-3 h-3 text-red-500" />
                              Refusée
                            </span>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => removeItem('demandes', idx)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
                {ficheData.demandes.length === 0 && (
                  <p className="text-sm text-muted-foreground italic text-center py-4">Aucune demande enregistrée</p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter className="mt-6">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Fermer
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Carte collaborateur
const CollaborateurCard = ({ collaborateur, onEdit, onDelete, onViewFiche }) => {
  const navigate = useNavigate();
  const [inviteCodeCopied, setInviteCodeCopied] = useState(false);
  const RoleIcon = ROLE_ICONS[collaborateur.role] || Users;

  const copyInviteCode = () => {
    navigator.clipboard.writeText(collaborateur.invite_code || '');
    setInviteCodeCopied(true);
    setTimeout(() => setInviteCodeCopied(false), 2000);
    toast.success('Code d\'invitation copié');
  };

  const initials = `${collaborateur.prenom?.[0] || ''}${collaborateur.nom?.[0] || ''}`.toUpperCase();

  return (
    <Card 
      className="card-hover cursor-pointer" 
      data-testid={`collaborateur-card-${collaborateur.id}`}
      onClick={() => navigate(`/team/${collaborateur.id}`)}
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <Avatar className="w-12 h-12 border-2 border-primary/20">
              <AvatarImage src={collaborateur.photo_url} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {collaborateur.photo_url ? null : <RoleIcon className="w-6 h-6" />}
                {!collaborateur.photo_url && initials}
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="font-semibold">{collaborateur.nom_complet}</h3>
              <Badge className={ROLE_COLORS[collaborateur.role]}>
                {collaborateur.role_info?.nom || collaborateur.role}
              </Badge>
            </div>
          </div>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" data-testid={`menu-${collaborateur.id}`} onClick={(e) => e.stopPropagation()}>
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); navigate(`/team/${collaborateur.id}`); }}>
                <Eye className="w-4 h-4 mr-2" />
                Fiche complète
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); onEdit(collaborateur); }}>
                <Edit className="w-4 h-4 mr-2" />
                Modifier
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); onDelete(collaborateur.id); }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Supprimer
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Mail className="w-4 h-4" />
            <span>{collaborateur.email}</span>
          </div>
          {collaborateur.telephone && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Phone className="w-4 h-4" />
              <span>{collaborateur.telephone}</span>
            </div>
          )}
        </div>

        {/* Permissions */}
        <div className="mt-4">
          <div className="flex flex-wrap gap-1">
            {(collaborateur.permissions || []).slice(0, 4).map((perm) => (
              <Badge key={perm} variant="outline" className="text-xs">
                {perm === 'all' ? 'Complet' : perm}
              </Badge>
            ))}
            {(collaborateur.permissions || []).length > 4 && (
              <Badge variant="outline" className="text-xs">
                +{collaborateur.permissions.length - 4}
              </Badge>
            )}
          </div>
        </div>

        {/* Statut */}
        <div className="mt-4 flex items-center justify-between text-sm">
          <div className="flex items-center gap-1">
            {collaborateur.is_active ? (
              <>
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-green-600 dark:text-green-400">Actif</span>
              </>
            ) : (
              <>
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-red-600 dark:text-red-400">Inactif</span>
              </>
            )}
          </div>
          {collaborateur.derniere_connexion && (
            <div className="flex items-center gap-1 text-muted-foreground">
              <Clock className="w-3 h-3" />
              <span className="text-xs">
                {new Date(collaborateur.derniere_connexion).toLocaleDateString('fr-FR')}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function TeamPage() {
  const { token } = useAuth();
  const { societes } = useWorkspace();
  const [collaborateurs, setCollaborateurs] = useState([]);
  const [roles, setRoles] = useState({});
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCollaborateur, setEditingCollaborateur] = useState(null);
  const [stats, setStats] = useState(null);
  const [ficheOpen, setFicheOpen] = useState(false);
  const [selectedCollaborateur, setSelectedCollaborateur] = useState(null);

  const handleViewFiche = (collaborateur) => {
    setSelectedCollaborateur(collaborateur);
    setFicheOpen(true);
  };

  // Charger les données
  useEffect(() => {
    const fetchData = async () => {
      if (!token) return;
      setLoading(true);
      
      try {
        // Charger les rôles et permissions
        const rolesRes = await fetch(`${API_URL}/api/team/roles`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (rolesRes.ok) {
          const data = await rolesRes.json();
          setRoles(data.roles || {});
          setPermissions(data.permissions || {});
        }

        // Charger les collaborateurs
        const collabRes = await fetch(`${API_URL}/api/team/collaborateurs`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (collabRes.ok) {
          const data = await collabRes.json();
          setCollaborateurs(data.collaborateurs || []);
        }

        // Charger les stats
        const statsRes = await fetch(`${API_URL}/api/team/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (statsRes.ok) {
          const data = await statsRes.json();
          setStats(data);
        }
      } catch (error) {
        console.error('Erreur chargement:', error);
        toast.error('Erreur lors du chargement des données');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [token]);

  // Sauvegarder un collaborateur
  const handleSaveCollaborateur = async (formData, collaborateurId) => {
    try {
      const url = collaborateurId 
        ? `${API_URL}/api/team/collaborateurs/${collaborateurId}`
        : `${API_URL}/api/team/collaborateurs`;
      
      const method = collaborateurId ? 'PUT' : 'POST';
      
      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const data = await res.json();
        
        if (collaborateurId) {
          setCollaborateurs(prev => 
            prev.map(c => c.id === collaborateurId ? { ...c, ...formData } : c)
          );
          toast.success('Collaborateur modifié');
        } else {
          setCollaborateurs(prev => [...prev, data.collaborateur]);
          if (data.invite_code) {
            toast.success(`Collaborateur invité. Code: ${data.invite_code}`, {
              duration: 10000,
              action: {
                label: 'Copier',
                onClick: () => navigator.clipboard.writeText(data.invite_code)
              }
            });
          } else {
            toast.success('Collaborateur ajouté');
          }
        }
        
        setEditingCollaborateur(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de l\'enregistrement');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  // Supprimer un collaborateur
  const handleDeleteCollaborateur = async (collaborateurId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce membre de l\'équipe ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/team/collaborateurs/${collaborateurId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        setCollaborateurs(prev => prev.filter(c => c.id !== collaborateurId));
        toast.success('Membre supprimé de l\'équipe');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Erreur:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Filtrer les collaborateurs
  const filteredCollaborateurs = collaborateurs.filter(c => {
    const matchSearch = 
      c.nom_complet?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchRole = filterRole === 'all' || c.role === filterRole;
    return matchSearch && matchRole;
  });

  const activeCollaborateurs = filteredCollaborateurs.filter(c => c.is_active);
  const inactiveCollaborateurs = filteredCollaborateurs.filter(c => !c.is_active);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="team-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Gestion de l'équipe</h1>
          <p className="text-muted-foreground">
            Gérez les accès et permissions de vos collaborateurs
          </p>
        </div>
        <Button onClick={() => { setEditingCollaborateur(null); setDialogOpen(true); }} data-testid="btn-add-collaborateur">
          <UserPlus className="w-4 h-4 mr-2" />
          Ajouter un collaborateur
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.collaborateurs?.total || 0}</p>
                  <p className="text-sm text-muted-foreground">Collaborateurs</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.interventions?.previsionnelles || 0}</p>
                  <p className="text-sm text-muted-foreground">Prévisionnelles</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.interventions?.realisees || 0}</p>
                  <p className="text-sm text-muted-foreground">Réalisées</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Shield className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats.interventions?.a_valider || 0}</p>
                  <p className="text-sm text-muted-foreground">À valider</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filtres */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un collaborateur..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9"
            data-testid="search-collaborateurs"
          />
        </div>
        <Select value={filterRole} onValueChange={setFilterRole}>
          <SelectTrigger className="w-full md:w-48" data-testid="filter-role">
            <SelectValue placeholder="Filtrer par rôle" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les rôles</SelectItem>
            {Object.entries(roles).map(([key, role]) => (
              <SelectItem key={key} value={key}>{role.nom}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Liste des collaborateurs actifs */}
      <div>
        <h2 className="text-lg font-semibold mb-4">
          Collaborateurs actifs ({activeCollaborateurs.length})
        </h2>
        {activeCollaborateurs.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {activeCollaborateurs.map((collab) => (
              <CollaborateurCard
                key={collab.id}
                collaborateur={collab}
                onEdit={(c) => { setEditingCollaborateur(c); setDialogOpen(true); }}
                onDelete={handleDeleteCollaborateur}
                onViewFiche={handleViewFiche}
              />
            ))}
          </div>
        ) : (
          <Card>
            <CardContent className="pt-6 text-center text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucun collaborateur trouvé</p>
              <Button 
                variant="link" 
                onClick={() => { setEditingCollaborateur(null); setDialogOpen(true); }}
              >
                Ajouter le premier collaborateur
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Liste des collaborateurs inactifs */}
      {inactiveCollaborateurs.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-4 text-muted-foreground">
            Collaborateurs inactifs ({inactiveCollaborateurs.length})
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 opacity-60">
            {inactiveCollaborateurs.map((collab) => (
              <CollaborateurCard
                key={collab.id}
                collaborateur={collab}
                onEdit={(c) => { setEditingCollaborateur(c); setDialogOpen(true); }}
                onDelete={handleDeleteCollaborateur}
                onViewFiche={handleViewFiche}
              />
            ))}
          </div>
        </div>
      )}

      {/* Dialog ajout/modification */}
      <CollaborateurDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        collaborateur={editingCollaborateur}
        roles={roles}
        permissions={permissions}
        societes={societes}
        onSave={handleSaveCollaborateur}
      />

      {/* Dialog fiche salarié */}
      <FicheSalarieDialog
        open={ficheOpen}
        onOpenChange={setFicheOpen}
        collaborateur={selectedCollaborateur}
        onUpdate={() => {
          // Recharger les collaborateurs
          fetch(`${API_URL}/api/team/collaborateurs`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }).then(res => res.json()).then(data => setCollaborateurs(data.collaborateurs || []));
        }}
      />
    </div>
  );
}
